import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/feature/SEOHead';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { categories, siteConfig } from '@/mocks/data';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || categories[0].slug);

  useEffect(() => {
    if (slug) {
      setActiveSlug(slug);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [slug]);

  const activeCategory = categories.find((c) => c.slug === activeSlug) || categories[0];

  return (
    <>
      <SchemaOrg
        type="WebPage"
        data={{
          name: `${activeCategory.name} - ${siteConfig.name}`,
          description: activeCategory.desc,
          url: `${siteConfig.url}/category/${activeSlug}`,
          inLanguage: 'zh-CN',
        }}
      />
      <SEOHead
        title={`${activeCategory.name} - ${siteConfig.name}`}
        description={activeCategory.desc}
        keywords={`${activeCategory.name},成人免费网站,正规资源,${siteConfig.keywords}`}
        canonicalUrl={`${siteConfig.url}/category/${activeSlug}`}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="bg-background-100 border-b border-background-200/70">
            <div className="max-w-7xl mx-auto">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: activeCategory.name },
                ]}
              />
              <div className="px-4 md:px-6 pb-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600">
                    <i className={`${activeCategory.icon} text-lg`}></i>
                  </div>
                  <h1 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading">
                    {activeCategory.name}
                  </h1>
                </div>
                <p className="text-sm text-foreground-600 ml-[52px]">
                  {activeCategory.desc}
                </p>
              </div>
            </div>
          </div>

          <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
            <div className="flex flex-col lg:flex-row gap-8">
              <aside className="w-full lg:w-56 flex-shrink-0">
                <div className="lg:sticky lg:top-20">
                  <h3 className="text-sm font-semibold text-foreground-900 mb-3">资源分类</h3>
                  <div className="flex lg:flex-col gap-1 overflow-x-auto lg:overflow-visible pb-2 lg:pb-0">
                    {categories.map((cat) => (
                      <Link
                        key={cat.slug}
                        to={`/category/${cat.slug}`}
                        className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm whitespace-nowrap transition-colors cursor-pointer ${
                          activeSlug === cat.slug
                            ? 'bg-primary-50 text-primary-700 font-medium'
                            : 'text-foreground-600 hover:bg-background-100 hover:text-foreground-900'
                        }`}
                      >
                        <i className={`${cat.icon} text-xs`}></i>
                        {cat.name}
                        <span className="text-xs text-foreground-400 ml-auto">
                          {cat.websites.length}
                        </span>
                      </Link>
                    ))}
                  </div>
                </div>
              </aside>

              <div className="flex-1 min-w-0">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {activeCategory.websites.map((site) => (
                    <a
                      key={site.id}
                      href={site.url}
                      target="_blank"
                      rel="nofollow noopener noreferrer"
                      className="group bg-background-50 border border-background-200/70 rounded-lg p-5 cursor-pointer transition-all duration-200 hover:border-primary-200 hover:translate-y-[-1px]"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <h3 className="text-sm md:text-base font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors">
                          {site.name}
                        </h3>
                        <i className="ri-external-link-line text-foreground-400 group-hover:text-primary-500 transition-colors flex-shrink-0 ml-2"></i>
                      </div>
                      <p className="text-xs md:text-sm text-foreground-600 leading-relaxed mb-3 line-clamp-2">
                        {site.desc}
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        {site.tags.map((tag) => (
                          <span
                            key={tag}
                            className="px-2 py-0.5 bg-secondary-100 text-secondary-700 text-xs rounded-md whitespace-nowrap"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </a>
                  ))}
                </div>

                {activeCategory.websites.length === 0 && (
                  <div className="text-center py-20">
                    <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
                      <i className="ri-inbox-line text-2xl"></i>
                    </div>
                    <p className="text-foreground-600">该分类暂无资源</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </main>
        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}